import React, { useState, KeyboardEvent } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Empty } from 'antd';

import './index.less';

import IbModal, { IbModalPosition } from '../common/IbModal';
import { getTranslateFunc } from '../../utils/stringUtil';
import IbButton from '../common/IbButton';
import AgentMessageToolModal from '../AgentMessageToolModal';
import { AgentMessageTools, AgentMessageToolSchema } from '../../../gamma-api';
import IbTabs from '../common/IbTabs';
import IbTabPane from '../common/IbTabPane';
import IbTypography from '../common/IbTypography';
import IbIcon from '../common/IbIcon';

const MAIN_CLASS_NAME = 'agent-message-tools-modal';
const TABS_CLASS_NAME = `${MAIN_CLASS_NAME}__tabs`;
const TOOLS_CLASS_NAME = `${MAIN_CLASS_NAME}__tools`;
const TOOL_ITEM_CLASS_NAME = `${TOOLS_CLASS_NAME}__item`;

const SKELETON_ROW_COUNT = 3;
const SKELETON_HEIGHT = 40;
const SKELETON_BORDER_RADIUS = 10;

export interface IAgentMessageToolsModalProps {
  toolsList?: AgentMessageTools[];
  visible: boolean;
  loading?: boolean;
  onCancel: () => void;
}

const AgentMessageToolsModal: React.FC<IAgentMessageToolsModalProps> = ({ toolsList, visible, loading, onCancel }) => {
  const [toolSchemaModalVisible, setToolSchemaModalVisible] = useState(false);
  const [selectedToolSchema, setSelectedToolSchema] = useState<AgentMessageToolSchema>();
  const [agentTabKey, setAgentTabKey] = useState<string>();

  const translate = getTranslateFunc('Agent: Testing chat: Tools modal:');

  const onToolSchemaModalCancel = () => setToolSchemaModalVisible(false);

  const onAgentTabChange = (key: string) => setAgentTabKey(key);

  const renderFooter = () => {
    return (
      <IbButton type="secondary" onClick={onCancel}>
        {translate('Cancel')}
      </IbButton>
    );
  };

  const openModalWithToolSchema = (toolSchema: AgentMessageToolSchema) => {
    setSelectedToolSchema(toolSchema);
    setToolSchemaModalVisible(true);
  };

  const onToolClick = (tool: AgentMessageToolSchema) => () => openModalWithToolSchema(tool);

  const onToolKeyDown = (tool: AgentMessageToolSchema) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      openModalWithToolSchema(tool);
    }
  };

  const renderToolItem = (tool: AgentMessageToolSchema) => (
    <div
      key={tool.name}
      className={TOOL_ITEM_CLASS_NAME}
      role="button"
      tabIndex={0}
      onClick={onToolClick(tool)}
      onKeyDown={onToolKeyDown(tool)}
    >
      <IbTypography.Paragraph>{tool.name}</IbTypography.Paragraph>
      <IbIcon iconName="code-brackets-two" />
    </div>
  );

  const renderEmptyView = () => <Empty description={translate('No data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  const renderToolList = (tools: AgentMessageToolSchema[]) => {
    if (!tools.length) {
      return renderEmptyView();
    }

    return <div className={TOOLS_CLASS_NAME}>{tools.map(renderToolItem)}</div>;
  };

  const renderModalContent = () => {
    if (!toolsList?.length) {
      return renderEmptyView();
    }

    if (toolsList.length > 1) {
      return (
        <IbTabs activeKey={agentTabKey} className={TABS_CLASS_NAME} onChange={onAgentTabChange}>
          {toolsList.map((tools) => (
            <IbTabPane key={tools.agent.id} tab={tools.agent.name} tabKey={tools.agent.id}>
              {renderToolList(tools.tools)}
            </IbTabPane>
          ))}
        </IbTabs>
      );
    }

    return renderToolList(toolsList[0].tools);
  };

  const renderSkeleton = () => {
    return (
      <div className={TOOLS_CLASS_NAME}>
        {Array.from(Array(SKELETON_ROW_COUNT).keys()).map((_, row) => (
          <Skeleton key={row} borderRadius={SKELETON_BORDER_RADIUS} height={SKELETON_HEIGHT} />
        ))}
      </div>
    );
  };

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      position={IbModalPosition.Centered}
      title={translate('Title')}
      visible={visible}
      onCancel={onCancel}
    >
      {loading ? renderSkeleton() : renderModalContent()}
      <AgentMessageToolModal
        toolSchema={selectedToolSchema}
        visible={toolSchemaModalVisible}
        onCancel={onToolSchemaModalCancel}
      />
    </IbModal>
  );
};

export default AgentMessageToolsModal;
